import { Component, computed, inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { LutService } from '../../../../../services/lut.service';
import { CompanyBoardService } from '../../../../../services/companies/company-board.service';
import {
  BoardResponseDto,
  BoardMemberDto,
  BoardExecutiveMemberDto,
  BoardUpsertRequest,
  BoardBoardMemberUpsertDto,
  BoardExecutiveMemberUpsertDto,
  BoardMemberDeleteRequest,
  BoardMemberDeleteDto,
  ExecutiveMemberDeleteDto
} from '../../../../../models/api/companyBoardMemberDto';
import { BoardMemberOccupation } from '../../../../../models/boardMemberOccupation';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear, selectCompanyName } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { Observable, of } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { AddBoardMemberDialogComponent, AddBoardMemberDialogData, AddBoardMemberDialogResult } from './add-board-member-dialog/add-board-member-dialog.component';
import { DeleteBoardMemberDialogComponent, DeleteBoardMemberDialogData, DeleteBoardMemberDialogResult } from './delete-board-member-dialog/delete-board-member-dialog.component';
import { ConnectionsDialogComponent, ConnectionsDialogData } from './connections-dialog/connections-dialog.component';
import { CompanyRepresentativeDialogComponent, CompanyRepresentativeDialogData } from './company-representative-dialog/company-representative-dialog.component';
import { DirectorFlat } from '../../../../../models/director';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';
import boardClassificationRules from '../../../../../utilities/boardClassificationRules.json';
import boardCeoDateRules from '../../../../../utilities/boardCeoDateRules.json';

interface ClassificationOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-board-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    RouterLink,
    ReadOnlyTabDirective,
  ],
  templateUrl: './board-tab.component.html',
  styleUrl: './board-tab.component.scss'
})
export class BoardTabComponent implements OnInit, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private boardService = inject(CompanyBoardService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private dialog = inject(MatDialog);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Board));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  companyName = this.store.selectSignal(selectCompanyName);
  showYearWarning = false;
  isBoardDetailsCarriedForward = false;
  showKeywords = true;
  companyBoardType: number = 0; // 0 = Unitary (Board of Directors), non-zero = Dual (Supervisory + Management)
  otherMeasurement: string = '';
  otherData: (BoardResponseDto & { boardType?: number; boardMembers: BoardMemberDto[]; executiveMembers: BoardExecutiveMemberDto[] }) | null = null;

  occupations: BoardMemberOccupation[] = [];
  classificationOptions: ClassificationOption[] = [
    { value: '0', label: 'Unknown' },
    { value: '1', label: 'Executive' },
    { value: '2', label: 'Non-Executive' },
    { value: '3', label: 'Independent' }
  ];

  boardMembers: BoardMemberDto[] = [];
  boardDetails: Partial<BoardResponseDto> = {};
  private deletedBoardMembers: BoardMemberDeleteDto[] = [];
  private deletedExecutiveMembers: ExecutiveMemberDeleteDto[] = [];
  boardDataSource = new MatTableDataSource<BoardMemberDto>([]);
  managementDataSource = new MatTableDataSource<BoardMemberDto>([]);
  executiveDataSource = new MatTableDataSource<BoardExecutiveMemberDto>([]);

  @ViewChild('boardSort') boardSort!: MatSort;
  @ViewChild('managementSort') managementSort!: MatSort;
  @ViewChild('executiveSort') executiveSort!: MatSort;

  displayedColumns: string[] = [
    'index', 'name', 'classification', 'occupation', 'companyRepresentative', 'tenure',
    'hire', 'ceoHireDate', 'directorAttends', 'meetingsTotal',
    'percentage', 'connections', 'delete'
  ];

  executiveDisplayedColumns: string[] = [
    'index', 'name', 'occupation', 'tenure',
    'hire', 'ceoHireDate', 'connections', 'delete'
  ];

  form: FormGroup = this.fb.group({
    rows: this.fb.array([]),
    managementRows: this.fb.array([]),
    executiveRows: this.fb.array([]),
    meetingsFrequency: [null],
    meetingsFrequencyManagement: [null],
    hasBoardGuidelines: [false],
    hasCodeOfEthics: [false],
    comments: [''],
    keywords: this.fb.group({
      meetings: [''],
      attendance: [''],
      executiveManagement: ['']
    })
  });

  get rowsArray(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  get managementRowsArray(): FormArray {
    return this.form.get('managementRows') as FormArray;
  }

  get executiveRowsArray(): FormArray {
    return this.form.get('executiveRows') as FormArray;
  }

  get boardAttendanceAverage(): number | null {
    const rows = this.rowsArray.controls;
    if (rows.length === 0) return null;
    const sum = rows.reduce((acc, group) => acc + (parseFloat(group.value.directorAttendsPercentage) || 0), 0);
    return sum / rows.length;
  }

  get managementAttendanceAverage(): number | null {
    const rows = this.managementRowsArray.controls;
    if (rows.length === 0) return null;
    const sum = rows.reduce((acc, group) => acc + (parseFloat(group.value.directorAttendsPercentage) || 0), 0);
    return sum / rows.length;
  }

  get hasChanges(): boolean {
    return this.form.dirty || this.deletedBoardMembers.length > 0 || this.deletedExecutiveMembers.length > 0;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('board'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'board', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'board', year: this.selectedYear }));
    }

    this.form.get('meetingsFrequency')!.valueChanges.subscribe(() => {
      this.rowsArray.controls.forEach(g => {
        if (this.toFiniteNumber(g.get('meetingsTotal')?.value) == null) {
          this.recalculateAttendance(g as FormGroup);
        }
      });
    });
    this.form.get('meetingsFrequencyManagement')!.valueChanges.subscribe(() => {
      this.managementRowsArray.controls.forEach(g => {
        if (this.toFiniteNumber(g.get('meetingsTotal')?.value) == null) {
          this.recalculateAttendance(g as FormGroup);
        }
      });
    });

    this.patchKeywords();
    this.loadLookupData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const meetings = this.keywords.find(k => k.type === KeywordType.BoardMeetings);
    const attendance = this.keywords.find(k => k.type === KeywordType.BoardAttendance);
    const executive = this.keywords.find(k => k.type === KeywordType.ExecutiveManagement);
    this.form.get('keywords')!.patchValue({
      meetings: meetings?.value ?? '',
      attendance: attendance?.value ?? '',
      executiveManagement: executive?.value ?? ''
    }, { emitEvent: false });
    this.form.markAsPristine();
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasBoardChanges = this.hasChanges && !hasKeywordChanges ? true :
      this.form.dirty && (
        this.rowsArray.dirty || this.managementRowsArray.dirty || this.executiveRowsArray.dirty ||
        this.form.get('meetingsFrequency')!.dirty || this.form.get('meetingsFrequencyManagement')!.dirty ||
        this.form.get('hasBoardGuidelines')!.dirty || this.form.get('hasCodeOfEthics')!.dirty ||
        this.form.get('comments')!.dirty
      ) || this.deletedBoardMembers.length > 0 || this.deletedExecutiveMembers.length > 0;

    if (hasBoardChanges && hasKeywordChanges) return 'board data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'board data';
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  onDelete(member: BoardMemberDto): void {
    if (!this.companyId) return;

    if (!member.boardMemberId) {
      this.removeLocalBoardMember(member);
      this.toast.success('Board member removed successfully');
      return;
    }

    const boardIdx = this.boardDataSource.data.indexOf(member);
    const occupationId = boardIdx !== -1
      ? this.rowsArray.at(boardIdx)?.get('occupationId')?.value
      : this.managementRowsArray.at(this.managementDataSource.data.indexOf(member))?.get('occupationId')?.value;

    const dialogData: DeleteBoardMemberDialogData = {
      members: [{
        id: member.boardMemberId,
        name: member.directorFullName || '',
        showCeoEndDate: this.shouldShowCeoDates(occupationId),
        endDate: member.endDate,
        ceoEndDate: member.ceoEndDate
      }],
      year: this.selectedYear,
      isExecutive: false
    };

    const dialogRef = this.dialog.open(DeleteBoardMemberDialogComponent, {
      width: '720px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: DeleteBoardMemberDialogResult | null) => {
      if (!result) return;

      const entry = result.members[0];
      this.deletedBoardMembers.push({
        boardMemberId: member.boardMemberId,
        detailsId: member.detailsId || undefined,
        endDate: entry?.endDate || undefined,
        ceoEndDate: entry?.ceoEndDate || undefined
      });

      this.removeLocalBoardMember(member);
      this.form.markAsDirty();
    });
  }

  onInsert(): void {
    this.openAddMemberDialog({
      targetType: 'board',
      boardType: this.companyBoardType === 0 ? 0 : 1
    });
  }

  onInsertManagement(): void {
    this.openAddMemberDialog({
      targetType: 'management',
      boardType: 2
    });
  }

  onInsertExecutive(): void {
    this.openAddMemberDialog({
      targetType: 'executive',
      boardType: 0
    });
  }

  private openAddMemberDialog(data: AddBoardMemberDialogData): void {
    const dialogRef = this.dialog.open(AddBoardMemberDialogComponent, {
      width: '980px',
      panelClass: 'connections-dialog-panel',
      data
    });

    dialogRef.afterClosed().subscribe((result: AddBoardMemberDialogResult | null) => {
      if (!result) return;
      const director = result.director;

      if (this.isDirectorAlreadyAdded(director.id)) {
        this.toast.error(`${director.fullName} is already a member of this board.`);
        return;
      }

      if (result.targetType === 'executive') {
        this.addExecutiveMember(director);
      } else if (result.targetType === 'management') {
        this.addBoardMember(director, this.managementDataSource, this.managementRowsArray, 'managementRows', 2);
      } else {
        const boardType = this.companyBoardType === 0 ? 0 : 1;
        this.addBoardMember(director, this.boardDataSource, this.rowsArray, 'rows', boardType);
      }
    });
  }

  private isDirectorAlreadyAdded(directorId: string): boolean {
    const boardIds = this.boardDataSource.data.map(m => m.directorId);
    const managementIds = this.managementDataSource.data.map(m => m.directorId);
    const executiveIds = this.executiveDataSource.data.map(m => m.directorId);
    return [...boardIds, ...managementIds, ...executiveIds].includes(directorId);
  }

  private addBoardMember(
    director: DirectorFlat,
    dataSource: MatTableDataSource<BoardMemberDto>,
    formArray: FormArray,
    formArrayName: string,
    boardType: number
  ): void {
    const newMember: BoardMemberDto = {
      year: parseInt(this.selectedYear),
      detailsId: '',
      companyId: this.companyId!,
      boardMemberId: '',
      directorId: director.id,
      boardType,
      directorFullName: director.fullName,
      directorYearOfBirth: director.yearOfBirth,
      directorGender: director.genderDisplay,
      directorNationalityId: director.nationalityId,
      directorProfessionId: director.professionId,
      opbCompanyConnections: [],
      otherCompanyMemberships: [],
      createdDate: '',
      createdBy: '',
      updatedDate: '',
      updatedBy: ''
    };

    dataSource.data = [...dataSource.data, newMember];
    const newRow = this.createBoardRowGroup(newMember);
    formArray.push(newRow);
    newRow.markAsDirty();
    this.form.markAsDirty();
  }

  private addExecutiveMember(director: DirectorFlat): void {
    const newMember: BoardExecutiveMemberDto = {
      year: parseInt(this.selectedYear),
      detailsId: '',
      companyId: this.companyId!,
      executiveMemberId: '',
      directorId: director.id,
      directorFullName: director.fullName,
      directorYearOfBirth: director.yearOfBirth,
      directorGender: director.genderDisplay,
      directorNationalityId: director.nationalityId,
      directorProfessionId: director.professionId,
      opbCompanyConnections: [],
      otherCompanyMemberships: [],
      createdDate: '',
      createdBy: '',
      updatedDate: '',
      updatedBy: ''
    };

    this.executiveDataSource.data = [...this.executiveDataSource.data, newMember];
    const newRow = this.createExecRowGroup(newMember);
    this.executiveRowsArray.push(newRow);
    newRow.markAsDirty();
    this.form.markAsDirty();
  }

  onDeleteExecutive(member: BoardExecutiveMemberDto): void {
    if (!this.companyId) return;

    if (!member.executiveMemberId) {
      this.removeLocalExecutiveMember(member);
      this.toast.success('Executive member removed successfully');
      return;
    }

    const idx = this.executiveDataSource.data.indexOf(member);
    const occupationId = idx !== -1
      ? this.executiveRowsArray.at(idx)?.get('occupationId')?.value
      : undefined;

    const dialogData: DeleteBoardMemberDialogData = {
      members: [{
        id: member.executiveMemberId,
        name: member.directorFullName || '',
        showCeoEndDate: this.shouldShowCeoDates(occupationId),
        endDate: member.endDate,
        ceoEndDate: member.ceoEndDate
      }],
      year: this.selectedYear,
      isExecutive: true
    };

    const dialogRef = this.dialog.open(DeleteBoardMemberDialogComponent, {
      width: '720px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: DeleteBoardMemberDialogResult | null) => {
      if (!result) return;

      const entry = result.members[0];
      this.deletedExecutiveMembers.push({
        executiveMemberId: member.executiveMemberId,
        detailsId: member.detailsId || undefined,
        endDate: entry?.endDate || undefined,
        ceoEndDate: entry?.ceoEndDate || undefined
      });

      this.removeLocalExecutiveMember(member);
      this.form.markAsDirty();
    });
  }

  // ---- Multi-delete: Board / Supervisory ----
  get selectedBoardMembers(): BoardMemberDto[] {
    return this.boardDataSource.data.filter((row: any) => row.highlighted);
  }

  onDeleteSelectedBoardMembers(): void {
    this.deleteSelectedMembers(this.selectedBoardMembers, this.boardDataSource, this.rowsArray, false);
  }

  // ---- Multi-delete: Management ----
  get selectedManagementMembers(): BoardMemberDto[] {
    return this.managementDataSource.data.filter((row: any) => row.highlighted);
  }

  onDeleteSelectedManagementMembers(): void {
    this.deleteSelectedMembers(this.selectedManagementMembers, this.managementDataSource, this.managementRowsArray, false);
  }

  // ---- Multi-delete: Executive ----
  get selectedExecutiveMembers(): BoardExecutiveMemberDto[] {
    return this.executiveDataSource.data.filter((row: any) => row.highlighted);
  }

  onDeleteSelectedExecutiveMembers(): void {
    if (!this.companyId) return;
    const selected = this.selectedExecutiveMembers;
    if (selected.length === 0) return;

    // Drop new (unsaved) rows immediately — no dates needed for them.
    const newOnes = selected.filter(m => !m.executiveMemberId);
    const saved = selected.filter(m => !!m.executiveMemberId);

    if (saved.length === 0) {
      newOnes.forEach(m => this.removeLocalExecutiveMember(m));
      this.form.markAsDirty();
      return;
    }

    const dialogData: DeleteBoardMemberDialogData = {
      members: saved.map(m => {
        const idx = this.executiveDataSource.data.indexOf(m);
        const occupationId = idx !== -1 ? this.executiveRowsArray.at(idx)?.get('occupationId')?.value : undefined;
        return {
          id: m.executiveMemberId,
          name: m.directorFullName || '',
          showCeoEndDate: this.shouldShowCeoDates(occupationId),
          endDate: m.endDate,
          ceoEndDate: m.ceoEndDate
        };
      }),
      year: this.selectedYear,
      isExecutive: true
    };

    const dialogRef = this.dialog.open(DeleteBoardMemberDialogComponent, {
      width: '720px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: DeleteBoardMemberDialogResult | null) => {
      if (!result) return;

      const datesById = new Map(result.members.map(r => [r.id, r]));
      saved.forEach(member => {
        const entry = datesById.get(member.executiveMemberId);
        this.deletedExecutiveMembers.push({
          executiveMemberId: member.executiveMemberId,
          detailsId: member.detailsId || undefined,
          endDate: entry?.endDate || undefined,
          ceoEndDate: entry?.ceoEndDate || undefined
        });
        this.removeLocalExecutiveMember(member);
      });
      newOnes.forEach(m => this.removeLocalExecutiveMember(m));

      this.form.markAsDirty();
    });
  }

  /** Shared multi-delete flow for the board/supervisory and management tables. */
  private deleteSelectedMembers(
    selected: BoardMemberDto[],
    dataSource: MatTableDataSource<BoardMemberDto>,
    formArray: FormArray,
    isExecutive: boolean
  ): void {
    if (!this.companyId) return;
    if (selected.length === 0) return;

    const newOnes = selected.filter(m => !m.boardMemberId);
    const saved = selected.filter(m => !!m.boardMemberId);

    if (saved.length === 0) {
      newOnes.forEach(m => this.removeLocalBoardMember(m));
      this.form.markAsDirty();
      return;
    }

    const dialogData: DeleteBoardMemberDialogData = {
      members: saved.map(m => {
        const idx = dataSource.data.indexOf(m);
        const occupationId = idx !== -1 ? formArray.at(idx)?.get('occupationId')?.value : undefined;
        return {
          id: m.boardMemberId,
          name: m.directorFullName || '',
          showCeoEndDate: this.shouldShowCeoDates(occupationId),
          endDate: m.endDate,
          ceoEndDate: m.ceoEndDate
        };
      }),
      year: this.selectedYear,
      isExecutive
    };

    const dialogRef = this.dialog.open(DeleteBoardMemberDialogComponent, {
      width: '720px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: DeleteBoardMemberDialogResult | null) => {
      if (!result) return;

      const datesById = new Map(result.members.map(r => [r.id, r]));
      saved.forEach(member => {
        const entry = datesById.get(member.boardMemberId);
        this.deletedBoardMembers.push({
          boardMemberId: member.boardMemberId,
          detailsId: member.detailsId || undefined,
          endDate: entry?.endDate || undefined,
          ceoEndDate: entry?.ceoEndDate || undefined
        });
        this.removeLocalBoardMember(member);
      });
      newOnes.forEach(m => this.removeLocalBoardMember(m));

      this.form.markAsDirty();
    });
  }

  toggleRowHighlight(row: any): void {
    row.highlighted = !row.highlighted;
  }

  getClassificationLabel(value: any): string {
    if (value == null) return '';
    return this.classificationOptions.find(o => o.value === String(value))?.label ?? '';
  }

  onOpenConnections(row: BoardMemberDto | BoardExecutiveMemberDto): void {
    const isExecutive = 'executiveMemberId' in row;
    const memberId = isExecutive ? (row as BoardExecutiveMemberDto).executiveMemberId : (row as BoardMemberDto).boardMemberId;

    if (!memberId || !this.companyId) return;

    const dialogData: ConnectionsDialogData = {
      memberName: row.directorFullName,
      memberId,
      companyId: this.companyId,
      year: parseInt(this.selectedYear),
      isExecutive,
      opbCompanyConnections: [...row.opbCompanyConnections],
      otherCompanyMemberships: [...row.otherCompanyMemberships]
    };

    const dialogRef = this.dialog.open(ConnectionsDialogComponent, {
      width: '900px',
      panelClass: 'connections-dialog-panel',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((changed: boolean) => {
      if (changed) {
        this.loadData();
      }
    });
  }

  getConnectionsCount(row: BoardMemberDto | BoardExecutiveMemberDto): number {
    return (row.opbCompanyConnections?.length || 0) + (row.otherCompanyMemberships?.length || 0);
  }

  getConnectionsTooltip(row: BoardMemberDto | BoardExecutiveMemberDto): string {
    const names: string[] = [
      ...(row.otherCompanyMemberships?.map(c => {
        const label = this.getMembershipLabel(c.isExecutive, c.boardType);
        return `${c.companyName} (${label})`;
      }) || []),
      ...(row.opbCompanyConnections?.map(c => c.opbCompanyName) || [])
    ];
    return names.join('\n');
  }

  private getMembershipLabel(isExecutive: boolean, boardType?: number): string {
    if (isExecutive) return 'Executive';
    switch (boardType) {
      case 1: return 'Supervisory';
      case 2: return 'Management';
      default: return 'Board';
    }
  }

  getOccupationName(id: string): string {
    return this.occupations.find(o => o.id === id)?.name ?? '';
  }

  getEndDateTooltip(row: any): string {
    const endDate = Helpers.toDisplayDate(row.endDate);
    return endDate ? `End Date: ${endDate}` : '';
  }

  getCeoEndDateTooltip(row: any): string {
    const ceoEndDate = Helpers.toDisplayDate(row.ceoEndDate);
    return ceoEndDate ? `CEO End Date: ${ceoEndDate}` : '';
  }

  computeTenure(hireDate: string): number | null {
    if (!hireDate) return null;
    const selectedYear = parseInt(this.selectedYear);
    // Year-only format (e.g. "1999")
    if (/^\d{4}$/.test(hireDate)) return selectedYear - parseInt(hireDate) + 1;
    // Handle dd/mm/yyyy format (from form values after sort rebuild)
    const ddMatch = hireDate.match(/^\d{2}\/\d{2}\/(\d{4})$/);
    if (ddMatch) return selectedYear - parseInt(ddMatch[1]) + 1;
    // Handle ISO format (YYYY-MM-DD...)
    const isoMatch = hireDate.match(/^(\d{4})-/);
    if (isoMatch) return selectedYear - parseInt(isoMatch[1]) + 1;
    return null;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }

  onBoardSort(sort: Sort): void {
    this.sortTable(sort, this.boardDataSource, this.rowsArray);
  }

  onManagementSort(sort: Sort): void {
    this.sortTable(sort, this.managementDataSource, this.managementRowsArray);
  }

  onExecutiveSort(sort: Sort): void {
    this.sortTable(sort, this.executiveDataSource, this.executiveRowsArray);
  }

  /**
   * The table's current sort, taken from its MatSort header so that rebuilding the
   * rows after a reload (e.g. a save) keeps the column the user picked instead of
   * snapping the data back to name/asc — which left the sort arrow pointing at one
   * column while the rows were ordered by another. Falls back to name/asc before the
   * header has rendered (first load).
   */
  private currentSort(sort: MatSort | undefined): Sort {
    return sort?.active && sort.direction
      ? { active: sort.active, direction: sort.direction }
      : { active: 'name', direction: 'asc' };
  }

  private sortTable<T>(sort: Sort, dataSource: MatTableDataSource<T>, formArray: FormArray): void {
    if (!sort.active || sort.direction === '') return;

    const dir = sort.direction === 'asc' ? 1 : -1;

    // Pair each row with its FormGroup, snapshotting current form values into the
    // data object so sorting sees in-progress edits.
    const paired = dataSource.data.map((row, i) => {
      const group = formArray.at(i) as FormGroup;
      return { merged: { ...row, ...group.getRawValue() }, group };
    });

    paired.sort((pa, pb) => {
      const a = pa.merged;
      const b = pb.merged;
      let valA: any;
      let valB: any;

      switch (sort.active) {
        case 'name':
          valA = a.directorFullName ?? '';
          valB = b.directorFullName ?? '';
          return dir * valA.localeCompare(valB);
        case 'classification':
          valA = parseInt(a.classification) || 99;
          valB = parseInt(b.classification) || 99;
          return dir * (valA - valB);
        case 'occupation':
          valA = this.getOccupationName(a.occupationId);
          valB = this.getOccupationName(b.occupationId);
          return dir * valA.localeCompare(valB);
        case 'tenure':
          valA = this.computeTenure(a.hireDate) ?? -Infinity;
          valB = this.computeTenure(b.hireDate) ?? -Infinity;
          return dir * (valA - valB);
        case 'hire':
          valA = a.hireDate ? new Date(a.hireDate).getTime() : 0;
          valB = b.hireDate ? new Date(b.hireDate).getTime() : 0;
          return dir * (valA - valB);
        case 'ceoHireDate':
          valA = a.ceoHireDate ? new Date(a.ceoHireDate).getTime() : 0;
          valB = b.ceoHireDate ? new Date(b.ceoHireDate).getTime() : 0;
          return dir * (valA - valB);
        case 'directorAttends':
          valA = a.directorAttends ?? -Infinity;
          valB = b.directorAttends ?? -Infinity;
          return dir * (valA - valB);
        case 'meetingsTotal':
          valA = a.meetingsTotal ?? -Infinity;
          valB = b.meetingsTotal ?? -Infinity;
          return dir * (valA - valB);
        case 'percentage':
          valA = a.directorAttendsPercentage ?? -Infinity;
          valB = b.directorAttendsPercentage ?? -Infinity;
          return dir * (valA - valB);
        default:
          return 0;
      }
    });

    // Reorder the data source and form array in lockstep, reusing the existing
    // FormGroup instances so per-row dirty state, values, and valueChanges
    // subscriptions survive the sort. Recreating the groups here would reset their
    // dirty flags, and performSave() only sends rows whose group is dirty — so any
    // edit made before a sort would be silently dropped on save.
    dataSource.data = paired.map(p => p.merged);
    const sortedRows = this.fb.array(paired.map(p => p.group));
    if (formArray === this.rowsArray) {
      this.form.setControl('rows', sortedRows);
    } else if (formArray === this.managementRowsArray) {
      this.form.setControl('managementRows', sortedRows);
    } else {
      this.form.setControl('executiveRows', sortedRows);
    }
  }

  private removeLocalBoardMember(member: BoardMemberDto): void {
    // Determine which data source and form array this member belongs to
    let dataSource: MatTableDataSource<BoardMemberDto>;
    let formArray: FormArray;
    let formArrayName: string;

    const boardIndex = this.boardDataSource.data.indexOf(member);
    if (boardIndex !== -1) {
      dataSource = this.boardDataSource;
      formArray = this.rowsArray;
      formArrayName = 'rows';
    } else {
      dataSource = this.managementDataSource;
      formArray = this.managementRowsArray;
      formArrayName = 'managementRows';
    }

    const index = dataSource.data.indexOf(member);
    if (index === -1) return;

    dataSource.data = dataSource.data.filter((_, i) => i !== index);
    formArray.removeAt(index);
    this.form.markAsDirty();
  }

  private removeLocalExecutiveMember(member: BoardExecutiveMemberDto): void {
    const index = this.executiveDataSource.data.indexOf(member);
    if (index === -1) return;

    this.executiveDataSource.data = this.executiveDataSource.data.filter((_, i) => i !== index);
    this.executiveRowsArray.removeAt(index);
    this.form.markAsDirty();
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'board', year }));
    this.loadData();
  }

  private loadLookupData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();

    this.lutService.getBoardMemberOccupations().subscribe({
      next: (occupations) => {
        this.occupations = occupations.sort((a, b) => a.name.localeCompare(b.name));
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.deletedBoardMembers = [];
    this.deletedExecutiveMembers = [];
    this.spinnerService.show();
    this.boardService.getAll(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const response = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.isBoardDetailsCarriedForward = resource.state === 'carried-forward';
        this.showYearWarning = this.isBoardDetailsCarriedForward || resource.state === 'absent';

        // Members come from context — populated in every state, including 'absent'.
        const boardMembers = resource.context?.boardMembers ?? [];
        const executiveMembers = resource.context?.executiveMembers ?? [];
        const boardType = resource.context?.boardType ?? 0;

        this.boardMembers = boardMembers;
        this.boardDetails = response ?? ({
          boardType,
          isBoardDetailsCarriedForward: this.isBoardDetailsCarriedForward
        } as BoardResponseDto);
        this.populateMemberTables(boardMembers, executiveMembers, boardType);

        if (response) {
          this.populateBoardLevelFields(response);
        } else {
          this.clearBoardLevelFields();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetTable();
        } else {
          this.toast.error('Error loading board data');
        }

        console.error('Board data error:', error);
      }
    });
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.boardService.getAll(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        if (resource.state === 'present' && resource.data) {
          this.otherData = {
            ...resource.data,
            boardType: resource.context?.boardType,
            boardMembers: resource.context?.boardMembers ?? [],
            executiveMembers: resource.context?.executiveMembers ?? []
          };
        } else {
          this.otherData = null;
        }
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  get otherBoardAttendanceAverage(): number | null {
    if (!this.otherData) return null;
    const members = this.otherData.boardType !== 0
      ? this.otherData.boardMembers.filter(m => m.boardType === 1)
      : this.otherData.boardMembers;
    const percentages = members
      .map(m => m.directorAttendsPercentage)
      .filter((v): v is number => v != null);
    if (percentages.length === 0) return null;
    return percentages.reduce((sum, v) => sum + v, 0) / percentages.length;
  }

  get otherManagementAttendanceAverage(): number | null {
    if (!this.otherData) return null;
    const members = this.otherData.boardMembers.filter(m => m.boardType === 2);
    const percentages = members
      .map(m => m.directorAttendsPercentage)
      .filter((v): v is number => v != null);
    if (percentages.length === 0) return null;
    return percentages.reduce((sum, v) => sum + v, 0) / percentages.length;
  }

  formatNumber(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  private populateMemberTables(members: BoardMemberDto[], executiveMembers: BoardExecutiveMemberDto[], boardType: number): void {
    this.companyBoardType = boardType;

    if (boardType !== 0) {
      // Dual: split by individual member boardType (1 = supervisory, 2 = management)
      const supervisory = members.filter(m => m.boardType === 1);
      const management = members.filter(m => m.boardType === 2);
      this.boardDataSource.data = supervisory;
      this.managementDataSource.data = management;
      this.buildFormArrays(supervisory, management);
    } else {
      // Unitary: all members in one table
      this.boardDataSource.data = members;
      this.managementDataSource.data = [];
      this.buildFormArrays(members, []);
    }

    this.executiveDataSource.data = executiveMembers;
    this.form.setControl('executiveRows', this.fb.array(
      executiveMembers.map(m => this.createExecRowGroup(m))
    ));

    this.onBoardSort(this.currentSort(this.boardSort));
    if (boardType !== 0) {
      this.onManagementSort(this.currentSort(this.managementSort));
    }
    if (executiveMembers.length > 0) {
      this.onExecutiveSort(this.currentSort(this.executiveSort));
    }
  }

  private populateBoardLevelFields(response: BoardResponseDto): void {
    this.form.patchValue({
      meetingsFrequency: response.meetingsFrequency ?? null,
      meetingsFrequencyManagement: response.meetingsFrequencyManagement ?? null,
      hasBoardGuidelines: response.hasBoardGuidelines ?? false,
      hasCodeOfEthics: response.hasCodeOfEthics ?? false,
      comments: response.comments ?? ''
    }, { emitEvent: false });
  }

  private clearBoardLevelFields(): void {
    this.form.patchValue({
      meetingsFrequency: null,
      meetingsFrequencyManagement: null,
      hasBoardGuidelines: false,
      hasCodeOfEthics: false,
      comments: ''
    }, { emitEvent: false });
  }

  private buildFormArrays(boardRows: BoardMemberDto[], managementRows: BoardMemberDto[]): void {
    const rows = this.fb.array(
      boardRows.map(m => this.createBoardRowGroup(m))
    );
    const mgmtRows = this.fb.array(
      managementRows.map(m => this.createBoardRowGroup(m))
    );

    this.form.setControl('rows', rows);
    this.form.setControl('managementRows', mgmtRows);
    this.form.markAsPristine();
  }

  onDateFocus(event: Event): void {
    Helpers.onDateInputFocus(event);
  }

  onDateBlur(event: Event): void {
    Helpers.onDateInputBlur(event);
  }

  onHireDateInput(event: Event, row: any): void {
    const value = Helpers.onDateInputFormat(event);
    row.hireDate = Helpers.toIsoDate(value);
  }

  onCeoHireDateInput(event: Event, row: any): void {
    const value = Helpers.onDateInputFormat(event);
    row.ceoHireDate = Helpers.toIsoDate(value);
  }

  private createBoardRowGroup(member: BoardMemberDto): FormGroup {
    const showCeo = this.shouldShowCeoDates(member.occupationId);
    const group = this.fb.group({
      detailsId: [member.detailsId],
      boardMemberId: [member.boardMemberId],
      directorId: [member.directorId],
      boardType: [member.boardType],
      classification: [member.classification ?? ''],
      occupationId: [member.occupationId || ''],
      companyRepresentative: [member.companyRepresentative ?? ''],
      hireDate: [Helpers.toDisplayDate(member.hireDate)],
      ceoHireDate: [showCeo ? Helpers.toDisplayDate(member.ceoHireDate) : ''],
      directorAttends: [member.directorAttends],
      meetingsTotal: [member.meetingsTotal],
      directorAttendsPercentage: [member.directorAttendsPercentage]
    });
    group.get('occupationId')!.valueChanges.subscribe(value => {
      this.applyClassificationRule(group, value as string);
      this.applyCeoDatesRule(group, value as string);
    });
    group.get('directorAttends')!.valueChanges.subscribe(() => this.recalculateAttendance(group));
    group.get('meetingsTotal')!.valueChanges.subscribe(() => this.recalculateAttendance(group));
    this.applyCeoDatesRule(group, member.occupationId ?? '');
    return group;
  }

  private recalculateAttendance(group: FormGroup): void {
    const attends = this.toFiniteNumber(group.get('directorAttends')!.value);
    // No attendance recorded for this member → leave the % untouched instead of zeroing it out.
    if (attends == null) {
      return;
    }
    const outOf = this.toFiniteNumber(group.get('meetingsTotal')!.value);

    let denominator: number;
    if (outOf != null) {
      denominator = outOf;
    } else {
      const isManagement = group.get('boardType')?.value === 2;
      denominator = this.toFiniteNumber(this.form.get(isManagement ? 'meetingsFrequencyManagement' : 'meetingsFrequency')!.value) ?? 0;
    }

    const percentage = denominator === 0 ? 0 : (attends / denominator) * 100;
    const rounded = Math.round(percentage);
    const percentageCtrl = group.get('directorAttendsPercentage')!;
    if (percentageCtrl.value !== rounded) {
      percentageCtrl.setValue(rounded);
      group.markAsDirty();
    }
  }

  private toFiniteNumber(value: any): number | null {
    if (value == null || value === '') return null;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isFinite(num) ? num : null;
  }

  private applyClassificationRule(group: FormGroup, occupationId: string): void {
    const occupation = this.occupations.find(o => o.id === occupationId);
    if (!occupation) return;
    const rules = boardClassificationRules.occupationToClassification as Record<string, string>;
    const matchKey = Object.keys(rules).find(k => k.toLowerCase() === occupation.name.toLowerCase());
    if (!matchKey) return;
    const target = this.classificationOptions.find(o => o.label.toLowerCase() === rules[matchKey].toLowerCase());
    if (!target) return;
    group.get('classification')!.setValue(target.value);
  }

  /** True when the given occupation is the "Company Representative" board occupation. */
  isCompanyRepresentativeOccupation(occupationId: string | null | undefined): boolean {
    if (!occupationId) return false;
    const occupation = this.occupations.find(o => o.id === occupationId);
    return occupation?.name?.trim().toLowerCase() === 'company representative';
  }

  /** Whether a row currently has company representative text stored. */
  hasCompanyRepresentative(group: AbstractControl): boolean {
    return !!group.get('companyRepresentative')?.value;
  }

  /** Opens the company representative editor dialog for a board/management row. */
  onEditCompanyRepresentative(group: AbstractControl, memberName: string): void {
    const control = group.get('companyRepresentative')!;
    const dialogData: CompanyRepresentativeDialogData = {
      memberName: memberName || '',
      value: control.value ?? ''
    };

    const dialogRef = this.dialog.open(CompanyRepresentativeDialogComponent, {
      width: '480px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result: string | null) => {
      if (result == null) return;
      if (result !== (control.value ?? '')) {
        control.setValue(result);
        control.markAsDirty();
        group.markAsDirty();
        this.form.markAsDirty();
      }
    });
  }

  shouldShowCeoDates(occupationId: string | null | undefined): boolean {
    if (!occupationId) return false;
    const occupation = this.occupations.find(o => o.id === occupationId);
    if (!occupation) return false;
    const allowed = boardCeoDateRules.ceoDateOccupations as string[];
    return allowed.some(name => name.toLowerCase() === occupation.name.toLowerCase());
  }

  private applyCeoDatesRule(group: FormGroup, occupationId: string): void {
    const ceoHireDateCtrl = group.get('ceoHireDate');
    if (!ceoHireDateCtrl) return;
    if (!this.shouldShowCeoDates(occupationId)) {
      ceoHireDateCtrl.setValue('', { emitEvent: false });
      ceoHireDateCtrl.disable({ emitEvent: false });
    } else if (ceoHireDateCtrl.disabled) {
      ceoHireDateCtrl.enable({ emitEvent: false });
    }
  }

  private createExecRowGroup(member: BoardExecutiveMemberDto): FormGroup {
    const showCeo = this.shouldShowCeoDates(member.occupationId);
    const group = this.fb.group({
      detailsId: [member.detailsId],
      executiveMemberId: [member.executiveMemberId],
      directorId: [member.directorId],
      occupationId: [member.occupationId || ''],
      hireDate: [Helpers.toDisplayDate(member.hireDate)],
      ceoHireDate: [showCeo ? Helpers.toDisplayDate(member.ceoHireDate) : ''],
      tenure: [member.tenure]
    });
    group.get('occupationId')!.valueChanges.subscribe(value => {
      this.applyCeoDatesRule(group, value as string);
    });
    this.applyCeoDatesRule(group, member.occupationId ?? '');
    return group;
  }

  private resetTable(): void {
    this.boardMembers = [];
    this.boardDataSource.data = [];
    this.managementDataSource.data = [];
    this.executiveDataSource.data = [];
    this.form.setControl('rows', this.fb.array([]));
    this.form.setControl('managementRows', this.fb.array([]));
    this.form.setControl('executiveRows', this.fb.array([]));
    this.form.markAsPristine();
  }

  /**
   * Resolves the appointment-level CEO/departure fields the per-row grid does not fully edit, so
   * the upsert — which overwrites them unconditionally — doesn't wipe them.
   * - `ceoHireDate` is editable only while the occupation shows it; when hidden the form control is
   *   blanked, so we resend the stored value instead of clearing it.
   * - `ceoEndDate` / `ceoTurnoverReasonId` / `endDate` are only ever set via the delete dialog,
   *   never the grid, so we always resend what was loaded.
   */
  private preservedCeoFields(
    member: BoardMemberDto | BoardExecutiveMemberDto,
    occupationId: string | null | undefined,
    formCeoHireDate: string
  ): Pick<BoardBoardMemberUpsertDto, 'ceoHireDate' | 'ceoEndDate' | 'ceoTurnoverReasonId' | 'endDate'> {
    // Stored dates come back from the GET without a timezone designator; re-normalize them to UTC
    // ISO (the same `...T00:00:00Z` form the edited date fields produce) so the backend doesn't
    // reject them — Npgsql refuses DateTime.Kind=Unspecified for `timestamp with time zone`.
    const toUtcIso = (d?: string): string | undefined => Helpers.toIsoDate(Helpers.toDisplayDate(d)) || undefined;
    return {
      ceoHireDate: this.shouldShowCeoDates(occupationId)
        ? (Helpers.toIsoDate(formCeoHireDate) || undefined)
        : toUtcIso(member.ceoHireDate),
      ceoEndDate: toUtcIso(member.ceoEndDate),
      ceoTurnoverReasonId: member.ceoTurnoverReasonId || undefined,
      endDate: toUtcIso(member.endDate)
    };
  }

  private performSave(): void {
    if (!this.companyId) return;

    const boardMembers: BoardBoardMemberUpsertDto[] = [];

    // Member details have open-ended ValidTo and naturally extend across years, so a
    // carry-forward view doesn't require resending unchanged rows — only dirty ones.
    this.rowsArray.controls.forEach((group, i) => {
      if (group.dirty) {
        const member = this.boardDataSource.data[i];
        const val = group.getRawValue();
        boardMembers.push({
          detailsId: val.detailsId || undefined,
          boardMemberId: val.boardMemberId,
          directorId: val.directorId || undefined,
          boardType: val.boardType ?? undefined,
          classification: val.classification,
          occupationId: val.occupationId,
          companyRepresentative: val.companyRepresentative || undefined,
          hireDate: Helpers.toIsoDate(val.hireDate) || undefined,
          ...this.preservedCeoFields(member, val.occupationId, val.ceoHireDate),
          directorAttends: val.directorAttends,
          meetingsTotal: val.meetingsTotal,
          directorAttendsPercentage: val.directorAttendsPercentage
        });
      }
    });

    // Collect dirty rows from management board
    this.managementRowsArray.controls.forEach((group, i) => {
      if (group.dirty) {
        const member = this.managementDataSource.data[i];
        const val = group.getRawValue();
        boardMembers.push({
          detailsId: val.detailsId || undefined,
          boardMemberId: val.boardMemberId,
          directorId: val.directorId || undefined,
          boardType: val.boardType ?? undefined,
          classification: val.classification,
          occupationId: val.occupationId,
          companyRepresentative: val.companyRepresentative || undefined,
          hireDate: Helpers.toIsoDate(val.hireDate) || undefined,
          ...this.preservedCeoFields(member, val.occupationId, val.ceoHireDate),
          directorAttends: val.directorAttends,
          meetingsTotal: val.meetingsTotal,
          directorAttendsPercentage: val.directorAttendsPercentage
        });
      }
    });

    // Collect dirty rows from executive management
    const executiveMembers: BoardExecutiveMemberUpsertDto[] = [];
    this.executiveRowsArray.controls.forEach((group, i) => {
      if (group.dirty) {
        const member = this.executiveDataSource.data[i];
        const val = group.value;
        executiveMembers.push({
          detailsId: val.detailsId || undefined,
          executiveMemberId: val.executiveMemberId,
          directorId: val.directorId || undefined,
          occupationId: val.occupationId,
          hireDate: Helpers.toIsoDate(val.hireDate) || undefined,
          ...this.preservedCeoFields(member, val.occupationId, val.ceoHireDate),
          tenure: val.tenure
        });
      }
    });

    // Board-level fields (per-year CompanyBoardDetails) are materialized for the year on
    // carry-forward saves so the year gets its own record.
    const boardLevelDirty = this.isBoardDetailsCarriedForward || ['meetingsFrequency', 'meetingsFrequencyManagement', 'hasBoardGuidelines', 'hasCodeOfEthics', 'comments']
      .some(field => this.form.get(field)?.dirty);

    // Build keyword upsert requests
    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, [
      { formField: 'meetings', type: KeywordType.BoardMeetings },
      { formField: 'attendance', type: KeywordType.BoardAttendance },
      { formField: 'executiveManagement', type: KeywordType.ExecutiveManagement }
    ], this.keywords);

    const hasBoardData = boardMembers.length > 0 || executiveMembers.length > 0 || this.deletedBoardMembers.length > 0 || this.deletedExecutiveMembers.length > 0 || boardLevelDirty;
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => k.type === KeywordType.BoardMeetings || k.type === KeywordType.BoardAttendance || k.type === KeywordType.ExecutiveManagement
    );

    if (!hasBoardData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();
    const formVal = this.form.value;
    const request: BoardUpsertRequest = {
      year: parseInt(this.selectedYear),
      meetingsFrequency: formVal.meetingsFrequency,
      meetingsFrequencyManagement: formVal.meetingsFrequencyManagement,
      hasBoardGuidelines: formVal.hasBoardGuidelines,
      hasCodeOfEthics: formVal.hasCodeOfEthics,
      comments: formVal.comments,
      boardMembers,
      executiveMembers,
      deletedBoardMembers: this.deletedBoardMembers,
      deletedExecutiveMembers: this.deletedExecutiveMembers
    };

    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasBoardData
      ? this.boardService.upsert(this.companyId!, request)
      : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_boardResult, keywordsResult]) => {
        if (hasBoardData) {
          this.showYearWarning = false;
          this.deletedBoardMembers = [];
          this.deletedExecutiveMembers = [];
        }
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
        if (hasBoardData) {
          this.loadData();
        }
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }
}
